import api from "./api";

// ===== EXISTING CRUD OPERATIONS =====
const createProduct = async (productData) => {
  // Check if productData is FormData (contains files)
  const isFormData = productData instanceof FormData;

  const config = isFormData
    ? { headers: { "Content-Type": "multipart/form-data" } }
    : {};

  const response = await api.post("/admin-product", productData, config);
  return response.data;
};

const updateProductById = async (id, productData) => {
  // Check if productData is FormData (contains files)
  const isFormData = productData instanceof FormData;

  const config = isFormData
    ? { headers: { "Content-Type": "multipart/form-data" } }
    : {};

  const response = await api.patch(`/admin-product/${id}`, productData, config);
  return response.data;
};

const updateProductStatusById = async (id, status) => {
  const response = await api.patch(`/admin-product/${id}/status`, { status });
  return response.data;
};

const deleteProductById = async (id) => {
  const response = await api.delete(`/admin-product/${id}`);
  return response.data;
};

const getProductById = async (id) => {
  const response = await api.get(`/admin-product/${id}`);
  return response.data;
};

const getAllProducts = async (filters) => {
  // Clean up filters before sending to API
  const params = { ...filters };

  // Map frontend "category" (singular) to backend "categories" (plural)
  if (params.category) {
    params.categories = params.category;
  }
  delete params.category;

  // Strip empty string values to keep query params clean
  Object.keys(params).forEach((key) => {
    if (
      params[key] === "" ||
      params[key] === null ||
      params[key] === undefined
    ) {
      delete params[key];
    }
  });

  const response = await api.get("/admin-product?includePricing=true", {
    params,
  });
  return response.data;
};

// ===== NEW PRICING OPERATIONS =====
const getProductWithPricing = async (id, options) => {
  const response = await api.get(`/admin-product/${id}/pricing`, {
    params: options,
  });
  return response.data;
};

// ===== NEW STOCK MANAGEMENT OPERATIONS =====
const updateLocationStock = async (id, stockData) => {
  const response = await api.patch(`/admin-product/${id}/stock`, stockData);
  return response.data;
};

const transferStock = async (transferData) => {
  const response = await api.post(
    "/admin-product/stock/transfer",
    transferData,
  );
  return response.data;
};

const getLowStockProducts = async (params) => {
  const response = await api.get("/admin-product/stock/low", { params });
  return response.data;
};

const reserveStock = async (reservationData) => {
  const response = await api.post(
    "/admin-product/stock/reserve",
    reservationData,
  );
  return response.data;
};

const checkAvailability = async (id, quantity) => {
  const response = await api.get(`/admin-product/${id}/availability`, {
    params: { quantity },
  });
  return response.data;
};

// ===== NEW BRANCH/STORE OPERATIONS =====
const getProductsByBranch = async (branchCode, params) => {
  const response = await api.get(`/admin-product/branch/${branchCode}`, {
    params,
  });
  return response.data;
};

const syncBranchInventory = async (inventoryData) => {
  const response = await api.post("/admin-product/branch/sync", inventoryData);
  return response.data;
};

// ===== NEW BULK OPERATIONS =====
const bulkUpdateProducts = async (updates) => {
  const response = await api.post("/admin-product/bulk-update", { updates });
  return response.data;
};

const importProducts = async (file, defaultBranch) => {
  const formData = new FormData();
  formData.append("file", file);
  if (defaultBranch) {
    formData.append("defaultBranch", defaultBranch);
  }

  const response = await api.post("/admin-product/import", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return response.data;
};

const exportProducts = async (params) => {
  const response = await api.get("/admin-product/export", {
    params,
    responseType: params.format === "json" ? "json" : "blob",
  });

  // If it's a file download (CSV/Excel), handle it differently
  if (params.format !== "json") {
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    const fileExtension = params.format === "excel" ? "xlsx" : "csv";
    link.setAttribute("download", `products-${Date.now()}.${fileExtension}`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    return { success: true, message: "File downloaded successfully" };
  }

  return response.data;
};

// ===== NEW HALLMARK OPERATIONS =====
const updateHallmark = async (id, hallmarkData) => {
  const response = await api.post(
    `/admin-product/${id}/hallmark`,
    hallmarkData,
  );
  return response.data;
};

const verifyHallmark = async (huid) => {
  const response = await api.get(`/admin-product/hallmark/verify/${huid}`);
  return response.data;
};

const adminProductService = {
  // Existing
  createProduct,
  updateProductById,
  updateProductStatusById,
  deleteProductById,
  getProductById,
  getAllProducts,

  // New - Pricing
  getProductWithPricing,

  // New - Stock Management
  updateLocationStock,
  transferStock,
  getLowStockProducts,
  reserveStock,
  checkAvailability,

  // New - Branch/Store
  getProductsByBranch,
  syncBranchInventory,

  // New - Bulk Operations
  bulkUpdateProducts,
  importProducts,
  exportProducts,

  // New - Hallmark
  updateHallmark,
  verifyHallmark,
};

export default adminProductService;
